import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import clsx from 'clsx';
import {
  CheckCircle2, CircleDashed, CircleDot, XCircle, Search, ChevronRight, ChevronDown, Plus, X, CalendarDays,
  Repeat2, AlertTriangle, FileText, Copy, Printer, Users, Layers, ExternalLink, Check, ListChecks, UserRound, Flag,
  BarChart3, Presentation, Minimize2, ChevronLeft, Clock,
} from 'lucide-react';
import type { Card, Colaborador, Flow } from '../../types';
import { apiUrl } from '../../utils/apiBase';

// 📋 Atas de reunião / Plano de ação (30/10/2026, pedido do André; visual refeito no mesmo dia, estilo Linear).
// Cada card é uma ação da ata. Três visões:
//  • Reunião: pauta do dia em lista densa (uma linha por ação), agrupada por setor ou responsável, atrasadas
//    primeiro; a linha abre um painel lateral com a linha do tempo do andamento e a edição (andamento do dia,
//    prazo, responsável, setor, Concluir / Sem novidade / Cancelar). "Gerar ata" monta o resumo da reunião.
//  • Por responsável: o INDICADOR da planilha (concluído / em aberto / em andamento / % + atrasadas).
//  • Minhas ações: só as do usuário logado.
// Grava pelas rotas /api/atas/<flow>/acoes (server.py). A aba só aparece em fluxo com "Vezes na ata".
// Só Tailwind + lucide-react + clsx (o que o frontend já tem; não há Radix/shadcn no projeto).

type Usuario = { id?: string; email?: string; nome?: string } | undefined;
interface Props {
  flow: Flow; cards: Card[]; colaboradores: Colaborador[]; currentUser: Usuario;
  onAbrirCard: (card: Card) => void; onCardSalvo: (card: Card) => void;
}

const norm = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const todosCampos = (flow: Flow) => (flow.etapas || []).flatMap(e => (e.campos || []).map(c => ({ ...c, etapaId: String(e.id) })));
export const fluxoTemAtas = (flow: Flow) => {
  const cs = todosCampos(flow).map(c => norm(c.nome));
  return cs.includes('vezes na ata') && cs.includes('reuniao de origem');
};

// ---------- texto ----------
const SIGLAS = new Set(['TI', 'T.I.', 'CD', 'CDS', "CD'S", 'CD´S', 'ES', 'SP', 'SC', 'RS', 'PR', 'MG', 'GO', 'BA', 'PE', 'RJ', 'DF', 'PB', 'PI',
  'GIX', 'SHX', 'ICMS', 'PIX', 'RH', 'DP', 'ND', 'NDS', 'NF', 'NFS', 'NFE', 'OC', 'OCS', 'ST', 'STS', 'WMS', 'SLA', 'KPI', 'ERP', 'CRM',
  'BI', 'PDF', 'UP', 'ML', 'MKT', 'POA', 'BH', 'CNPJ', 'CPF', 'TCL', 'LG', 'EPI', 'EPIS', 'DRE', 'DIFAL', 'GNRE', 'B2B', 'B2C', 'SKU', 'SKUS']);
// Texto todo em caixa alta vira "sentence case" (mantém siglas); texto já misto fica como está.
const frase = (s: string) => {
  const t = (s || '').trim();
  if (!t || /[a-zà-ú]/.test(t)) return t;
  let inicio = true;
  return t.split(/(\s+)/).map(w => {
    if (/^\s+$/.test(w)) return w;
    const limpo = w.replace(/[.,;:!?()]/g, '');
    let out = SIGLAS.has(limpo) ? w : w.toLowerCase();
    if (inicio && !SIGLAS.has(limpo)) out = out.charAt(0).toUpperCase() + out.slice(1);
    inicio = /[.!?]$/.test(w);
    return out;
  }).join('');
};
const nomeProprio = (s: string) => (s || '').toLowerCase().replace(/(^|\s|\.)(\p{L})/gu, (_, a, b) => a + b.toUpperCase());
const iniciais = (s: string) => (s || '?').replace(/[^\p{L}\s]/gu, '').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';
const CORES_AVATAR = ['bg-indigo-100 text-indigo-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-800', 'bg-sky-100 text-sky-700',
  'bg-rose-100 text-rose-700', 'bg-violet-100 text-violet-700', 'bg-teal-100 text-teal-700', 'bg-orange-100 text-orange-700'];
const corAvatar = (s: string) => { let h = 0; for (const ch of s || '') h = (h * 31 + ch.charCodeAt(0)) | 0; return CORES_AVATAR[Math.abs(h) % CORES_AVATAR.length]; };
function Avatar({ nome, size = 20 }: { nome: string; size?: number }) {
  return <span className={clsx('inline-grid place-items-center rounded-full font-semibold flex-shrink-0', corAvatar(nome))}
    style={{ width: size, height: size, fontSize: size <= 20 ? 9 : 11 }}>{iniciais(nome)}</span>;
}

// ---------- datas ----------
const ymdDe = (v: unknown) => { const s = String(v ?? '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : ''; };
const ymdHoje = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dt = (ymd: string) => { const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d); };
const dias = (de: string, ate: string) => Math.round((+dt(ate) - +dt(de)) / 86400000);
const br = (ymd: string) => (ymd ? `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}/${ymd.slice(0, 4)}` : '');
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const curta = (ymd: string) => (ymd ? `${Number(ymd.slice(8, 10))} ${MESES[Number(ymd.slice(5, 7)) - 1]}${ymd.slice(0, 4) !== ymdHoje().slice(0, 4) ? ' ' + ymd.slice(2, 4) : ''}` : '');
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const plural = (n: number, s: string, p: string) => `${n} ${n === 1 ? s : p}`;

// ---------- situação ----------
type Sit = 'aberto' | 'andamento' | 'concluido' | 'cancelado';
const SIT: Record<Sit, { nome: string; icone: ReactNode; pill: string; cor: string }> = {
  aberto: { nome: 'Em aberto', icone: <CircleDashed className="w-3.5 h-3.5" />, pill: 'text-amber-700 bg-amber-50 ring-amber-200', cor: 'text-amber-600' },
  andamento: { nome: 'Em andamento', icone: <CircleDot className="w-3.5 h-3.5" />, pill: 'text-indigo-700 bg-indigo-50 ring-indigo-200', cor: 'text-indigo-600' },
  concluido: { nome: 'Concluído', icone: <CheckCircle2 className="w-3.5 h-3.5" />, pill: 'text-emerald-700 bg-emerald-50 ring-emerald-200', cor: 'text-emerald-600' },
  cancelado: { nome: 'Cancelado', icone: <XCircle className="w-3.5 h-3.5" />, pill: 'text-gray-500 bg-gray-100 ring-gray-200', cor: 'text-gray-400' },
};
const ORDEM_SIT: Sit[] = ['aberto', 'andamento', 'concluido', 'cancelado'];

interface Acao {
  card: Card; id: string; titulo: string; texto: string; setor: string; respId: string; respNome: string; sit: Sit;
  prazo: string; inclusao: string; origem: string; vezes: number; ultima: string; concluidoEm: string; andamento: string;
  atrasada: boolean; diasAtraso: number; diasAberto: number; ultimaNota: { data: string; texto: string } | null;
  conclusao: string; estimada: boolean;
}

// Histórico "dd/mm[/aa] - texto": uma entrada por data; linhas sem data continuam a anterior.
const RE_ENTRADA = /^\s*(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s*[-–:]?\s*/;
const entradas = (txt: string) => {
  const out: { data: string; texto: string }[] = [];
  (txt || '').split('\n').forEach(l => {
    const m = l.match(RE_ENTRADA);
    if (m) out.push({ data: m[1], texto: l.slice(m[0].length).trim() });
    else if (l.trim()) {
      if (out.length) out[out.length - 1].texto = (out[out.length - 1].texto + ' ' + l.trim()).trim();
      else out.push({ data: '', texto: l.trim() });
    }
  });
  return out;
};
const entradaDoDia = (txt: string, ymd: string) => entradas(txt).filter(e => e.data === br(ymd)).map(e => e.texto).join(' · ');

const lerLocal = (k: string, padrao: string) => { try { return localStorage.getItem(k) || padrao; } catch { return padrao; } };
const gravarLocal = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sem storage */ } };

// ---------- peças de interface ----------
function useForaClique(ref: RefObject<HTMLElement>, aberto: boolean, fechar: () => void) {
  useEffect(() => {
    if (!aberto) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) fechar(); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
    document.addEventListener('mousedown', h); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [aberto, fechar, ref]);
}

function Popover({ gatilho, children, alinhar = 'left', largura = 'w-56' }: { gatilho: (abrir: () => void, aberto: boolean) => ReactNode; children: (fechar: () => void) => ReactNode; alinhar?: 'left' | 'right'; largura?: string }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fechar = () => setAberto(false);
  useForaClique(ref, aberto, fechar);
  return (
    <div ref={ref} className="relative inline-block" onClick={e => e.stopPropagation()}>
      {gatilho(() => setAberto(v => !v), aberto)}
      {aberto && (
        <div className={clsx('absolute z-40 mt-1 rounded-lg border border-gray-200 bg-white p-1 shadow-lg shadow-gray-900/5', largura, alinhar === 'right' ? 'right-0' : 'left-0')}>
          {children(fechar)}
        </div>)}
    </div>);
}
const ItemMenu = ({ ativo, onClick, children }: { ativo?: boolean; onClick: () => void; children: ReactNode }) => (
  <button onClick={onClick} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-gray-700 hover:bg-gray-100">
    <span className="flex flex-1 items-center gap-2 min-w-0">{children}</span>{ativo && <Check className="h-3.5 w-3.5 text-gray-500" />}
  </button>);

function StatusPill({ sit, onMudar, compacto }: { sit: Sit; onMudar: (s: Sit) => void; compacto?: boolean }) {
  return (
    <Popover largura="w-44" gatilho={(abrir) => (
      <button onClick={abrir} title={`${SIT[sit].nome} · clique para mudar`}
        className={clsx('inline-flex items-center gap-1.5 rounded-full text-[12px] font-medium whitespace-nowrap',
          compacto ? clsx('p-1 hover:bg-gray-100', SIT[sit].cor) : clsx('px-2 py-0.5 ring-1 ring-inset hover:brightness-95', SIT[sit].pill))}>
        {SIT[sit].icone}{!compacto && SIT[sit].nome}</button>)}>
      {fechar => ORDEM_SIT.map(s => (
        <ItemMenu key={s} ativo={s === sit} onClick={() => { fechar(); if (s !== sit) onMudar(s); }}>
          <span className={clsx('inline-flex', SIT[s].cor)}>{SIT[s].icone}</span>{SIT[s].nome}</ItemMenu>))}
    </Popover>);
}

function PrazoChip({ a }: { a: Acao }) {
  const fechada = a.sit === 'concluido' || a.sit === 'cancelado';
  if (!a.prazo) return <span className="text-[12px] text-gray-300">—</span>;
  return (
    <span title={a.atrasada ? `Venceu em ${br(a.prazo)} · ${a.diasAtraso} dias de atraso` : `Prazo ${br(a.prazo)}`}
      className={clsx('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] whitespace-nowrap',
        a.atrasada ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 font-medium' : fechada ? 'text-gray-400' : 'text-gray-600')}>
      {a.atrasada ? <AlertTriangle className="h-3 w-3" /> : <CalendarDays className="h-3 w-3 opacity-60" />}{curta(a.prazo)}</span>);
}

function Kpi({ t, v, s, tom, onClick, ativo }: { t: string; v: string | number; s?: string; tom?: 'red' | 'green' | 'indigo'; onClick?: () => void; ativo?: boolean }) {
  const cor = tom === 'red' ? 'text-red-600' : tom === 'green' ? 'text-emerald-600' : tom === 'indigo' ? 'text-indigo-600' : 'text-gray-900';
  return (
    <button onClick={onClick} disabled={!onClick}
      className={clsx('rounded-lg border bg-white px-3 py-2.5 text-left transition-colors', ativo ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200',
        onClick ? 'hover:border-gray-300' : 'cursor-default')}>
      <div className="text-[11px] font-medium text-gray-500">{t}</div>
      <div className={clsx('mt-0.5 text-xl font-semibold tabular-nums leading-tight', cor)}>{v}</div>
      {s && <div className="text-[11px] text-gray-400 truncate">{s}</div>}
    </button>);
}

function Segmentado<T extends string>({ valor, opcoes, onChange }: { valor: T; opcoes: [T, string, ReactNode?][]; onChange: (v: T) => void }) {
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
      {opcoes.map(([k, l, ic]) => (
        <button key={k} onClick={() => onChange(k)}
          className={clsx('inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors',
            valor === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800')}>{ic}{l}</button>))}
    </div>);
}

// ======================================================================================
export default function AtasFluxo({ flow, cards, colaboradores, currentUser, onAbrirCard, onCardSalvo }: Props) {
  const [visao, setVisao] = useState<'reuniao' | 'responsaveis' | 'minhas' | 'indicadores'>(() => (lerLocal('atas_visao', 'reuniao') as any));
  useEffect(() => gravarLocal('atas_visao', visao), [visao]);
  const [erro, setErro] = useState('');
  const hoje = ymdHoje();

  const campo = useMemo(() => {
    const cs = todosCampos(flow);
    const ache = (n: string) => cs.find(c => norm(c.nome) === norm(n))?.id || '';
    return { acao: ache('Ação'), setor: ache('Setor'), resp: ache('Responsável'), respNome: ache('Responsável na ata'),
      prazo: ache('Prazo'), inclusao: ache('Data de inclusão'), origem: ache('Reunião de origem'), vezes: ache('Vezes na ata'),
      ultima: ache('Última reunião'), andamento: ache('Andamento'), concluido: ache('Concluído em'),
      setorOpcoes: (cs.find(c => norm(c.nome) === 'setor')?.opcoes || []) as string[] };
  }, [flow]);
  const sitDaEtapa = useMemo(() => {
    const m = new Map<string, Sit>();
    (flow.etapas || []).forEach(e => {
      const n = norm(e.nome);
      m.set(String(e.id), /conclu/.test(n) ? 'concluido' : /cancel/.test(n) ? 'cancelado' : /andamento/.test(n) ? 'andamento' : 'aberto');
    });
    return m;
  }, [flow]);
  const colabPorId = useMemo(() => new Map(colaboradores.map(c => [String(c.id || (c as any)._id), c])), [colaboradores]);

  const acoes = useMemo<Acao[]>(() => cards.map(card => {
    const v = (card.campos || {}) as Record<string, unknown>;
    const s = (id: string) => (id && v[id] != null ? String(v[id]) : '');
    const sit = sitDaEtapa.get(String(card.etapaId)) || 'aberto';
    const respId = s(campo.resp);
    const respNome = nomeProprio((colabPorId.get(respId)?.nome || s(campo.respNome) || '').trim());
    const prazo = ymdDe(v[campo.prazo]);
    const inclusao = ymdDe(v[campo.inclusao]) || ymdDe((card as any).createdAt);
    const concluidoEm = ymdDe(v[campo.concluido]);
    const aberta = sit === 'aberto' || sit === 'andamento';
    const diasAtraso = aberta && prazo && prazo < hoje ? dias(prazo, hoje) : 0;
    const andamento = s(campo.andamento);
    const hist = entradas(andamento);
    const texto = s(campo.acao) || card.titulo;
    // data de conclusão: "Concluído em"; sem ela, a última entrada na etapa Concluído (marcada como estimada)
    let conclusao = sit === 'concluido' ? concluidoEm : '';
    let estimada = v.__conclusaoEstimada === true || (card as any).conclusaoEstimada === true;
    if (sit === 'concluido' && !conclusao) {
      const mov = ((card as any).etapaHistory || []).filter((h: any) => sitDaEtapa.get(String(h.etapaId)) === 'concluido');
      conclusao = ymdDe(String(mov.length ? mov[mov.length - 1].movedAt : '').slice(0, 10)); estimada = true;
    }
    return { card, id: String(card.id), titulo: frase(texto.split('\n')[0]), texto: frase(texto),
      setor: s(campo.setor) || 'OUTRO', respId, respNome: respNome || 'Sem responsável', sit, prazo, inclusao,
      origem: ymdDe(v[campo.origem]) || inclusao, vezes: Math.max(1, Number(v[campo.vezes]) || 1),
      ultima: ymdDe(v[campo.ultima]) || inclusao, concluidoEm, andamento, conclusao, estimada,
      atrasada: diasAtraso > 0, diasAtraso, diasAberto: inclusao ? dias(inclusao, aberta ? hoje : (concluidoEm || hoje)) : 0,
      ultimaNota: hist.length ? hist[hist.length - 1] : null };
  }), [cards, campo, sitDaEtapa, colabPorId, hoje]);

  const opcoesResp = useMemo(() => {
    const usados = new Set<string>();
    const soNome = new Set<string>();
    acoes.forEach(a => {
      if (a.respId && colabPorId.has(a.respId)) usados.add(a.respId);
      else if (a.respNome !== 'Sem responsável') soNome.add(a.respNome);
    });
    const ativos = colaboradores.filter(c => String((c as any).status || 'ativo').toLowerCase() !== 'inativo');
    const ord = (l: Colaborador[]) => l.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
    return { frequentes: ord([...usados].map(id => colabPorId.get(id)!).filter(Boolean)), soNome: [...soNome].sort(),
      todos: ord(ativos.filter(c => !usados.has(String(c.id)))) };
  }, [acoes, colaboradores, colabPorId]);
  const setores = useMemo(() => {
    const s = new Set<string>(campo.setorOpcoes);
    acoes.forEach(a => s.add(a.setor));
    return [...s].filter(Boolean).sort();
  }, [acoes, campo.setorOpcoes]);

  const cabecalhos = () => ({ 'Content-Type': 'application/json',
    'X-User-Data': JSON.stringify({ id: currentUser?.id || '', email: currentUser?.email || '' }) });
  const chamar = async (url: string, body: Record<string, unknown>) => {
    setErro('');
    try {
      const r = await fetch(apiUrl(url), { method: 'POST', headers: cabecalhos(), body: JSON.stringify(body) });
      const j = await r.json();
      if (!j.success) { setErro(j.error || 'não salvou'); return false; }
      onCardSalvo(j.card);
      return true;
    } catch { setErro('sem conexão com o servidor'); return false; }
  };
  const salvar = (cardId: string, body: Record<string, unknown>) => chamar(`/atas/${flow.id}/acoes/${cardId}`, body);
  const criar = (body: Record<string, unknown>) => chamar(`/atas/${flow.id}/acoes`, body);

  const meuColab = useMemo(() => {
    const em = norm(currentUser?.email || '');
    return em ? colaboradores.find(c => norm(c.email || '') === em) : undefined;
  }, [colaboradores, currentUser?.email]);

  const [abertoId, setAbertoId] = useState<string | null>(null);
  const [novaAberta, setNovaAberta] = useState(false);
  const [reuniao, setReuniao] = useState(() => { const v = lerLocal('atas_reuniao', ''); return v && v >= hoje ? v : hoje; });
  useEffect(() => gravarLocal('atas_reuniao', reuniao), [reuniao]);

  // ordem das linhas na tela (a lista visível informa) → "Próxima / Anterior" no painel
  const ordemRef = useRef<string[]>([]);
  const navegar = (dir: 1 | -1) => {
    const l = ordemRef.current;
    const i = abertoId ? l.indexOf(abertoId) : -1;
    const j = i + dir;
    if (j >= 0 && j < l.length) setAbertoId(l[j]);
  };

  // 🖥️ Modo apresentação: tela cheia + tudo 25% maior, para projetar na reunião
  const raiz = useRef<HTMLDivElement>(null);
  const [apresentar, setApresentar] = useState(false);
  const alternarApresentar = async () => {
    if (!apresentar) {
      setApresentar(true);
      try { await raiz.current?.requestFullscreen?.(); } catch { /* sem tela cheia: segue só com a fonte maior */ }
    } else {
      setApresentar(false);
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* ok */ }
    }
  };
  useEffect(() => {
    const h = () => { if (!document.fullscreenElement) setApresentar(false); };
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  // atalhos: "/" busca · "n" nova ação · "j"/"k" próxima/anterior com o painel aberto
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement;
      if (e.ctrlKey || e.metaKey || e.altKey || /^(INPUT|TEXTAREA|SELECT)$/.test(alvo?.tagName) || alvo?.isContentEditable) return;
      if (e.key === '/') { const b = raiz.current?.querySelector<HTMLInputElement>('input[data-atas-busca]'); if (b) { e.preventDefault(); b.focus(); } }
      else if (e.key === 'n' && !novaAberta && visao !== 'indicadores') { e.preventDefault(); setNovaAberta(true); }
      else if (abertoId && (e.key === 'j' || e.key === 'ArrowDown')) { e.preventDefault(); navegar(1); }
      else if (abertoId && (e.key === 'k' || e.key === 'ArrowUp')) { e.preventDefault(); navegar(-1); }
    };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  });

  const ctx: Ctx = { acoes, opcoesResp, setores, salvar, criar, onAbrirCard, hoje, abrir: setAbertoId,
    reuniao: visao === 'reuniao' ? reuniao : undefined, setOrdem: (ids) => { ordemRef.current = ids; } };
  const aberta = abertoId ? acoes.find(a => a.id === abertoId) : undefined;
  const posicao = abertoId ? ordemRef.current.indexOf(abertoId) : -1;

  return (
    <div ref={raiz} className={clsx('min-h-full min-w-0 overflow-x-clip text-gray-900', apresentar ? 'atas-apresentar overflow-y-auto bg-white' : 'bg-gray-50/60')}
      style={apresentar ? { zoom: 1.25, height: '100%', backgroundColor: '#ffffff' } as CSSProperties : undefined}>
      {apresentar && (
        // tela cheia: fundo branco (o padrão do navegador é preto, e o cinza translúcido virava cinza-escuro)
        // e textos secundários mais escuros para aguentar o projetor
        <style>{`.atas-apresentar::backdrop{background:#fff}
          .atas-apresentar .text-gray-300{color:#6b7280}.atas-apresentar .text-gray-400{color:#4b5563}
          .atas-apresentar .text-gray-500{color:#374151}.atas-apresentar .text-gray-600{color:#1f2937}
          .atas-apresentar .border-gray-100{border-color:#e5e7eb}
          .atas-apresentar .atas-tabelas{grid-template-columns:minmax(0,1fr)}`}</style>)}
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur px-4 md:px-6">
        <div className="flex flex-wrap items-center gap-3 py-2.5">
          <Segmentado valor={visao} onChange={setVisao} opcoes={[
            ['reuniao', 'Reunião', <ListChecks key="r" className="h-3.5 w-3.5" />],
            ['responsaveis', 'Por responsável', <Users key="p" className="h-3.5 w-3.5" />],
            ['minhas', 'Minhas ações', <UserRound key="m" className="h-3.5 w-3.5" />],
            ['indicadores', 'Indicadores', <BarChart3 key="i" className="h-3.5 w-3.5" />]]} />
          {erro && <span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1 text-[12px] text-red-700"><AlertTriangle className="h-3.5 w-3.5" />{erro}</span>}
          <div className="ml-auto flex items-center gap-2">
            {visao === 'reuniao' && (
              <label className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-600">
                <CalendarDays className="h-3.5 w-3.5 text-gray-400" />Reunião
                <input type="date" value={reuniao} onChange={e => e.target.value && setReuniao(e.target.value)}
                  className="border-0 bg-transparent p-0 text-[12px] font-medium text-gray-900 focus:ring-0" /></label>)}
            <button onClick={alternarApresentar} title={apresentar ? 'Sair da apresentação (Esc)' : 'Tela cheia com letra maior, para projetar'}
              className={clsx('inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-medium',
                apresentar ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50')}>
              {apresentar ? <Minimize2 className="h-3.5 w-3.5" /> : <Presentation className="h-3.5 w-3.5" />}{apresentar ? 'Sair' : 'Apresentar'}</button>
            {visao !== 'indicadores' && (
              <button onClick={() => setNovaAberta(true)} title="Nova ação (N)"
                className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-gray-800">
                <Plus className="h-3.5 w-3.5" />Nova ação</button>)}
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-4">
        {visao === 'reuniao' && <ModoReuniao ctx={ctx} reuniao={reuniao} />}
        {visao === 'responsaveis' && <PorResponsavel ctx={ctx} />}
        {visao === 'minhas' && <MinhasAcoes ctx={ctx} colab={meuColab} />}
        {visao === 'indicadores' && <Indicadores ctx={ctx} />}
      </div>

      {aberta && <Painel key={aberta.id} a={aberta} ctx={ctx} onFechar={() => setAbertoId(null)}
        nav={{ pos: posicao, total: ordemRef.current.length, ir: navegar }} />}
      {novaAberta && <NovaAcao ctx={ctx} reuniao={visao === 'reuniao' ? reuniao : hoje}
        respPadrao={visao === 'minhas' && meuColab ? `c:${meuColab.id}` : ''} onFechar={() => setNovaAberta(false)} />}
    </div>
  );
}

type Ctx = {
  acoes: Acao[]; hoje: string; setores: string[]; reuniao?: string;
  opcoesResp: { frequentes: Colaborador[]; soNome: string[]; todos: Colaborador[] };
  salvar: (cardId: string, body: Record<string, unknown>) => Promise<boolean>;
  criar: (body: Record<string, unknown>) => Promise<boolean>;
  onAbrirCard: (card: Card) => void; abrir: (id: string) => void;
  setOrdem: (ids: string[]) => void;
};

// valor do <select> de responsável: "c:<colaboradorId>" ou "n:<NOME DA ATA>"
const respValor = (a: { respId: string; respNome: string }, temColab: (id: string) => boolean) =>
  a.respId && temColab(a.respId) ? `c:${a.respId}` : a.respNome && a.respNome !== 'Sem responsável' ? `n:${a.respNome.toUpperCase()}` : '';
const respBody = (v: string) => (v.startsWith('c:') ? { responsavelId: v.slice(2), responsavelNome: '' } : { responsavelId: '', responsavelNome: v.slice(2) });
const campoCls = 'w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[13px] text-gray-900 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100';

function SelectResp({ value, onChange, op, className }: { value: string; onChange: (v: string) => void; op: Ctx['opcoesResp']; className?: string }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={className || campoCls}>
      <option value="">Escolher responsável…</option>
      {op.frequentes.length > 0 && <optgroup label="Nas atas">{op.frequentes.map(c => <option key={c.id} value={`c:${c.id}`}>{nomeProprio(c.nome)}</option>)}</optgroup>}
      {op.soNome.length > 0 && <optgroup label="Só o nome da ata">{op.soNome.map(n => <option key={n} value={`n:${n.toUpperCase()}`}>{n}</option>)}</optgroup>}
      <optgroup label="Outros colaboradores">{op.todos.map(c => <option key={c.id} value={`c:${c.id}`}>{nomeProprio(c.nome)}</option>)}</optgroup>
    </select>);
}

const ordenarAcoes = (l: Acao[]) => [...l].sort((x, y) =>
  (Number(y.atrasada) - Number(x.atrasada)) || (y.diasAtraso - x.diasAtraso)
  || (Number(x.sit === 'concluido' || x.sit === 'cancelado') - Number(y.sit === 'concluido' || y.sit === 'cancelado'))
  || ((x.prazo || '9999') < (y.prazo || '9999') ? -1 : (x.prazo || '9999') > (y.prazo || '9999') ? 1 : 0));

// ---------- linha densa ----------
function Linha({ a, ctx, mostrarResp = true }: { a: Acao; ctx: Ctx; mostrarResp?: boolean }) {
  const r = ctx.reuniao;
  const revista = !!r && a.ultima === r && a.origem !== r;
  const nova = !!r && a.origem === r;
  const fechada = a.sit === 'concluido' || a.sit === 'cancelado';
  return (
    <div role="button" tabIndex={0} onClick={() => ctx.abrir(a.id)} onKeyDown={e => e.key === 'Enter' && ctx.abrir(a.id)}
      className="group flex h-10 cursor-pointer items-center gap-3 border-b border-gray-100 px-3 text-[13px] last:border-b-0 hover:bg-gray-50 focus:bg-indigo-50/40 focus:outline-none">
      <StatusPill sit={a.sit} compacto onMudar={s => ctx.salvar(a.id, { situacao: SIT[s].nome, ...(r ? { reuniao: r } : {}) })} />
      <span className={clsx('min-w-0 flex-1 truncate', fechada ? 'text-gray-400 line-through decoration-gray-300' : 'text-gray-900')} title={a.texto}>{a.titulo}</span>
      {nova && <span className="hidden sm:inline rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700">nova</span>}
      {revista && <span className="hidden sm:inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"><Check className="h-3 w-3" />revista</span>}
      <span title={a.vezes > 1 ? `Tratada em ${a.vezes} reuniões` : 'Primeira vez na ata'}
        className={clsx('hidden md:inline-flex w-12 items-center justify-end gap-1 text-[12px] tabular-nums', a.vezes >= 5 ? 'text-orange-600' : 'text-gray-400')}>
        {a.vezes > 1 && <><Repeat2 className="h-3 w-3" />{a.vezes}×</>}</span>
      {mostrarResp && (
        <span className="hidden sm:flex w-40 items-center gap-1.5 truncate text-[12px] text-gray-600"><Avatar nome={a.respNome} /><span className="truncate">{a.respNome}</span></span>)}
      <span className="w-20 text-right"><PrazoChip a={a} /></span>
      <span className="hidden lg:block w-16 text-right text-[12px] tabular-nums text-gray-400" title={a.ultimaNota ? `Última anotação: ${a.ultimaNota.texto}` : 'Sem anotação'}>
        {a.ultimaNota?.data ? a.ultimaNota.data.slice(0, 5) : '—'}</span>
    </div>);
}

function Grupo({ titulo, avatar, lista, ctx, mostrarResp }: { titulo: string; avatar?: boolean; lista: Acao[]; ctx: Ctx; mostrarResp: boolean }) {
  const [aberto, setAberto] = useState(true);
  const atr = lista.filter(a => a.atrasada).length;
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <button onClick={() => setAberto(v => !v)} className={clsx('flex w-full items-center gap-2 bg-gray-50/80 px-3 py-2 text-left', aberto ? 'rounded-t-lg border-b border-gray-100' : 'rounded-lg')}>
        {aberto ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
        {avatar && <Avatar nome={titulo} />}
        <span className="text-[13px] font-semibold text-gray-800">{avatar ? titulo : frase(titulo)}</span>
        <span className="text-[12px] text-gray-400 tabular-nums">{lista.length}</span>
        {atr > 0 && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 text-[11px] font-medium text-red-600"><AlertTriangle className="h-3 w-3" />{atr}</span>}
      </button>
      {aberto && <div>{ordenarAcoes(lista).map(a => <Linha key={a.id} a={a} ctx={ctx} mostrarResp={mostrarResp} />)}</div>}
    </section>);
}

function CabecalhoColunas({ mostrarResp = true }: { mostrarResp?: boolean }) {
  return (
    <div className="hidden sm:flex items-center gap-3 px-3 text-[11px] font-medium uppercase tracking-wide text-gray-400">
      <span className="w-6" /><span className="flex-1">Ação</span><span className="hidden md:block w-12 text-right">Na ata</span>
      {mostrarResp && <span className="w-40">Responsável</span>}<span className="w-20 text-right">Prazo</span><span className="hidden lg:block w-16 text-right">Últ. nota</span>
    </div>);
}

// ---------- filtros ----------
function ChipFiltro({ rotulo, valor, opcoes, onChange, icone }: { rotulo: string; valor: string; opcoes: { v: string; l: string; av?: boolean }[]; onChange: (v: string) => void; icone: ReactNode }) {
  const [q, setQ] = useState('');
  const sel = opcoes.find(o => o.v === valor);
  return (
    <Popover largura="w-64" gatilho={(abrir) => (
      <button onClick={abrir} className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px]',
        valor ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-dashed border-gray-300 bg-white text-gray-600 hover:border-gray-400')}>
        {icone}{rotulo}{sel && <><span className="text-indigo-300">:</span><span className="max-w-[140px] truncate font-medium">{sel.l}</span></>}
        {valor && <X className="h-3 w-3" onClick={e => { e.stopPropagation(); onChange(''); }} />}
      </button>)}>
      {fechar => (
        <div>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrar…" className="mb-1 w-full rounded-md border border-gray-200 px-2 py-1 text-[12px] focus:outline-none" />
          <div className="max-h-64 overflow-auto">
            {opcoes.filter(o => norm(o.l).includes(norm(q))).map(o => (
              <ItemMenu key={o.v} ativo={o.v === valor} onClick={() => { onChange(o.v); fechar(); }}>
                {o.av && <Avatar nome={o.l} size={18} />}<span className="truncate">{o.l}</span></ItemMenu>))}
          </div>
        </div>)}
    </Popover>);
}

// filtro vindo de um cartão de KPI: aparece como chip junto dos outros e sai no "Limpar"
type FiltroKpi = { rotulo: string; limpar: () => void } | undefined;

// Cartões por situação (Por responsável e Minhas ações): clicar filtra a lista; clicar de novo limpa.
type FSit = '' | 'aberto' | 'andamento' | 'atrasadas' | 'concluido' | 'pendentes';
const ROT_FSIT: Record<Exclude<FSit, ''>, string> = { aberto: 'Em aberto', andamento: 'Em andamento', atrasadas: 'Atrasadas', concluido: 'Concluídas', pendentes: 'Pendentes' };
// 03/11/2026 André: cada ação numa situação só — Em aberto e Em andamento = no prazo; Atrasadas = vencidas;
// Pendentes = soma dos três (tudo que não está concluído).
const pend = (a: Acao) => a.sit === 'aberto' || a.sit === 'andamento';
const aplicaFSit = (l: Acao[], f: FSit) => (!f ? l : l.filter(a => f === 'atrasadas' ? pend(a) && a.atrasada
  : f === 'pendentes' ? pend(a) : (f === 'aberto' || f === 'andamento') ? a.sit === f && !a.atrasada : a.sit === f));
const kpiDe = (f: FSit, set: (f: FSit) => void): FiltroKpi => (f ? { rotulo: ROT_FSIT[f], limpar: () => set('') } : undefined);
function KpisSituacao({ l, f, set, pendentes }: { l: Acao[]; f: FSit; set: (f: FSit) => void; pendentes?: boolean }) {
  const c = l.filter(a => a.sit === 'concluido').length;
  const ab = l.filter(a => a.sit === 'aberto' && !a.atrasada).length, an = l.filter(a => a.sit === 'andamento' && !a.atrasada).length;
  const atr = l.filter(a => pend(a) && a.atrasada).length;
  const p = pct(c, l.length);
  const fx = (k: FSit) => () => set(f === k ? '' : k);
  return (
    <div className={clsx('grid grid-cols-2 gap-2', pendentes ? 'sm:grid-cols-4' : 'sm:grid-cols-6')}>
      <Kpi t="% concluído" v={`${p}%`} s={`${c} de ${l.length} · ver todas`} tom={p >= 90 ? 'green' : p < 75 ? 'red' : undefined} onClick={() => set('')} ativo={f === ''} />
      {pendentes
        ? <Kpi t="Pendentes" v={ab + an + atr} s={`${ab} em aberto · ${an} em andamento · ${atr} atrasadas`} onClick={fx('pendentes')} ativo={f === 'pendentes'} />
        : <><Kpi t="Pendentes" v={ab + an + atr} s={`${ab} em aberto · ${an} em andamento · ${atr} atrasadas`} onClick={fx('pendentes')} ativo={f === 'pendentes'} />
          <Kpi t="Em aberto" v={ab} onClick={fx('aberto')} ativo={f === 'aberto'} /><Kpi t="Em andamento" v={an} tom="indigo" onClick={fx('andamento')} ativo={f === 'andamento'} /></>}
      <Kpi t="Atrasadas" v={atr} s="prazo vencido" tom={atr ? 'red' : undefined} onClick={fx('atrasadas')} ativo={f === 'atrasadas'} />
      <Kpi t="Concluídas" v={c} tom="green" onClick={fx('concluido')} ativo={f === 'concluido'} />
    </div>);
}

function ListaAgrupada({ lista, ctx, padrao = 'setor', vazio, kpi }: { lista: Acao[]; ctx: Ctx; padrao?: 'setor' | 'resp' | 'nenhum'; vazio: string; kpi?: FiltroKpi }) {
  const [agrup, setAgrup] = useState<'setor' | 'resp' | 'nenhum'>(() => (lerLocal('atas_agrup', padrao) as any));
  useEffect(() => gravarLocal('atas_agrup', agrup), [agrup]);
  const [busca, setBusca] = useState('');
  const [setor, setSetor] = useState('');
  const [resp, setResp] = useState('');
  const [soAtras, setSoAtras] = useState(false);
  const b = norm(busca);
  const vis = lista.filter(a => (!setor || a.setor === setor) && (!resp || a.respNome === resp) && (!soAtras || a.atrasada)
    && (!b || norm(a.texto + ' ' + a.respNome + ' ' + a.andamento).includes(b)));
  const grupos = useMemo(() => {
    if (agrup === 'nenhum') return [{ nome: '', l: vis, atr: 0 }];
    const m = new Map<string, Acao[]>();
    vis.forEach(a => { const k = agrup === 'setor' ? a.setor : a.respNome; m.set(k, [...(m.get(k) || []), a]); });
    return [...m.entries()].map(([nome, l]) => ({ nome, l, atr: l.filter(a => a.atrasada).length }))
      .sort((x, y) => (y.atr - x.atr) || x.nome.localeCompare(y.nome));
  }, [vis, agrup]); // eslint-disable-line react-hooks/exhaustive-deps
  ctx.setOrdem(vis.length ? grupos.flatMap(g => ordenarAcoes(g.l).map(a => a.id)) : []);
  const setoresL = [...new Set(lista.map(a => a.setor))].sort().map(s => ({ v: s, l: frase(s) }));
  const respsL = [...new Set(lista.map(a => a.respNome))].sort().map(s => ({ v: s, l: s, av: true }));
  const limpar = setor || resp || soAtras || busca || kpi;
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {kpi && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-2.5 py-1 text-[12px] font-medium text-white">
            {kpi.rotulo}<button onClick={kpi.limpar} title="Tirar este filtro" className="opacity-70 hover:opacity-100"><X className="h-3 w-3" /></button></span>)}
        <label className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input data-atas-busca value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar ação, pessoa, andamento… ( / )"
            className="w-56 md:w-72 rounded-full border border-gray-200 bg-white py-1 pl-8 pr-3 text-[12px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100" /></label>
        <ChipFiltro rotulo="Setor" valor={setor} onChange={setSetor} opcoes={setoresL} icone={<Layers className="h-3 w-3" />} />
        <ChipFiltro rotulo="Responsável" valor={resp} onChange={setResp} opcoes={respsL} icone={<UserRound className="h-3 w-3" />} />
        <button onClick={() => setSoAtras(v => !v)} className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px]',
          soAtras ? 'border-red-200 bg-red-50 text-red-700' : 'border-dashed border-gray-300 bg-white text-gray-600 hover:border-gray-400')}>
          <Flag className="h-3 w-3" />Só atrasadas</button>
        {limpar && <button onClick={() => { setSetor(''); setResp(''); setSoAtras(false); setBusca(''); kpi?.limpar(); }} className="text-[12px] text-gray-400 hover:text-gray-700">Limpar</button>}
        <div className="ml-auto flex items-center gap-2 text-[12px] text-gray-500">
          <span className="hidden sm:inline">{plural(vis.length, 'ação', 'ações')} · agrupar</span>
          <Segmentado valor={agrup} onChange={setAgrup} opcoes={[['setor', 'Setor'], ['resp', 'Responsável'], ['nenhum', 'Nenhum']]} />
        </div>
      </div>
      {vis.length > 0 && <CabecalhoColunas mostrarResp={agrup !== 'resp'} />}
      <div className="space-y-2.5">
        {vis.length > 0 && grupos.map(g => agrup === 'nenhum'
          ? <div key="todos" className="rounded-lg border border-gray-200 bg-white">{ordenarAcoes(g.l).map(a => <Linha key={a.id} a={a} ctx={ctx} />)}</div>
          : <Grupo key={g.nome} titulo={g.nome} avatar={agrup === 'resp'} lista={g.l} ctx={ctx} mostrarResp={agrup !== 'resp'} />)}
        {!vis.length && <div className="rounded-lg border border-dashed border-gray-200 bg-white py-12 text-center text-[13px] text-gray-400">{vazio}</div>}
      </div>
    </div>);
}

// ---------- visões ----------
function ModoReuniao({ ctx, reuniao }: { ctx: Ctx; reuniao: string }) {
  const [filtro, setFiltro] = useState<'' | 'atrasadas' | 'pendentes' | 'revistas' | 'novas'>('');
  const [ata, setAta] = useState(false);
  const daReuniao = (a: Acao) => a.ultima === reuniao || a.origem === reuniao;
  const pauta = ctx.acoes.filter(a => a.sit === 'aberto' || a.sit === 'andamento' || daReuniao(a));
  const abertas = pauta.filter(a => a.sit === 'aberto' || a.sit === 'andamento');
  const revistas = pauta.filter(a => a.ultima === reuniao && a.origem !== reuniao);
  const novas = pauta.filter(a => a.origem === reuniao);
  const atrasadas = abertas.filter(a => a.atrasada);
  const pendentes = abertas.filter(a => a.ultima !== reuniao);
  const fechadasHoje = pauta.filter(a => (a.sit === 'concluido' || a.sit === 'cancelado') && a.ultima === reuniao).length;
  const base = abertas.length + fechadasHoje;
  const progresso = pct(base - pendentes.length, base);
  const lista = filtro === 'atrasadas' ? atrasadas : filtro === 'pendentes' ? pendentes : filtro === 'revistas' ? revistas : filtro === 'novas' ? novas : pauta;
  const fx = (f: typeof filtro) => () => setFiltro(filtro === f ? '' : f);
  return (
    <div className="space-y-4">
      <div className="grid gap-2 lg:grid-cols-[1fr_300px]">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Kpi t="Em pauta" v={abertas.length} s="abertas e em andamento" onClick={() => setFiltro('')} ativo={filtro === ''} />
          <Kpi t="Atrasadas" v={atrasadas.length} s="prazo vencido" tom={atrasadas.length ? 'red' : undefined} onClick={fx('atrasadas')} ativo={filtro === 'atrasadas'} />
          <Kpi t="Revistas hoje" v={revistas.length} s={`${pendentes.length} falta${pendentes.length === 1 ? '' : 'm'} revisar`} tom="green" onClick={fx('revistas')} ativo={filtro === 'revistas'} />
          <Kpi t="Novas" v={novas.length} s="entraram nesta reunião" tom="indigo" onClick={fx('novas')} ativo={filtro === 'novas'} />
        </div>
        <div className="flex flex-col justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
          <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
            <span>Reunião de {br(reuniao)}</span><span className="tabular-nums text-gray-900">{progresso}% revista</span></div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100"><i className="block h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progresso}%` }} /></div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={fx('pendentes')} className={clsx('text-[12px]', filtro === 'pendentes' ? 'font-medium text-indigo-700' : 'text-gray-500 hover:text-gray-800')}>
              {filtro === 'pendentes' ? 'Mostrando as pendentes' : `Ver as ${pendentes.length} pendentes`}</button>
            <button onClick={() => setAta(true)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 text-[12px] font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50">
              <FileText className="h-3.5 w-3.5" />Gerar ata</button>
          </div>
        </div>
      </div>
      <ListaAgrupada lista={lista} ctx={ctx} vazio="Nada na pauta com esse filtro."
        kpi={filtro ? { rotulo: { atrasadas: 'Atrasadas', pendentes: 'Falta revisar', revistas: 'Revistas hoje', novas: 'Novas' }[filtro], limpar: () => setFiltro('') } : undefined} />
      {ata && <GerarAta ctx={ctx} reuniao={reuniao} onFechar={() => setAta(false)} />}
    </div>);
}

function PorResponsavel({ ctx }: { ctx: Ctx }) {
  const [ano, setAno] = useState('todos');
  const [por, setPor] = useState<'resp' | 'setor'>('resp');
  const [sel, setSel] = useState<string | null>(null);   // '*' = todas as pessoas (clique num cartão da visão geral)
  // 03/11/2026 André: abre em Pendentes (em aberto + em andamento, atrasadas inclusas, sem concluídas)
  const [fs, setFs] = useState<FSit>('pendentes');
  useEffect(() => { if (!sel) setFs('pendentes'); }, [sel]);
  // 03/11/2026 André: ordenar as colunas da tabela (clique no título) e drill (clique no número abre a pessoa filtrada)
  type ColOrd = 'nome' | 'c' | 'pend' | 'ab' | 'an' | 'total' | 'p' | 'atr';
  const [ord, setOrd] = useState<{ col: ColOrd; desc: boolean }>({ col: 'pend', desc: true });
  const anos = useMemo(() => [...new Set(ctx.acoes.map(a => a.inclusao.slice(0, 4)).filter(Boolean))].sort().reverse(), [ctx.acoes]);
  const base = ctx.acoes.filter(a => a.sit !== 'cancelado' && (ano === 'todos' || a.inclusao.startsWith(ano)));
  const linhas = useMemo(() => {
    const m = new Map<string, Acao[]>();
    base.forEach(a => { const k = por === 'resp' ? a.respNome : a.setor; m.set(k, [...(m.get(k) || []), a]); });
    return [...m.entries()].map(([nome, l]) => {
      const c = l.filter(a => a.sit === 'concluido').length, ab = l.filter(a => a.sit === 'aberto' && !a.atrasada).length, an = l.filter(a => a.sit === 'andamento' && !a.atrasada).length;
      return { nome, l, c, ab, an, total: l.length, p: pct(c, l.length), atr: l.filter(a => pend(a) && a.atrasada).length };
    }).map(x => ({ ...x, pend: x.ab + x.an + x.atr })).sort((x, y) => {
      const k = ord.col;
      const d = k === 'nome' ? x.nome.localeCompare(y.nome) : ((x as any)[k] - (y as any)[k]);
      return (ord.desc ? -d : d) || (y.pend - x.pend) || (y.total - x.total);
    });
  }, [base, por, ord]); // eslint-disable-line react-hooks/exhaustive-deps
  const drill = (nome: string, f: FSit) => (e: { stopPropagation: () => void }) => { e.stopPropagation(); setSel(nome); setFs(f); };
  const Th = ({ col, t, esq }: { col: ColOrd; t: string; esq?: boolean }) => (
    <th onClick={() => setOrd(o => ({ col, desc: o.col === col ? !o.desc : col !== 'nome' }))}
      className={clsx('cursor-pointer select-none px-2 py-2 font-medium hover:text-gray-700', esq ? 'px-3 text-left' : 'text-right', ord.col === col && 'text-gray-700')}>
      {t}{ord.col === col ? (ord.desc ? ' ↓' : ' ↑') : ''}</th>);
  const Num = ({ nome, f, v, cls }: { nome: string; f: FSit; v: number; cls?: string }) => (
    <button onClick={drill(nome, f)} disabled={!v} title={v ? 'Ver essas ações' : undefined}
      className={clsx('rounded px-1 tabular-nums', v ? 'hover:bg-indigo-50 hover:underline' : 'cursor-default', cls)}>{v}</button>);
  const tot = linhas.reduce((s, r) => ({ c: s.c + r.c, ab: s.ab + r.ab, an: s.an + r.an, total: s.total + r.total, atr: s.atr + r.atr }), { c: 0, ab: 0, an: 0, total: 0, atr: 0 });
  const r = sel === '*' ? { nome: 'Todos', l: base } : sel ? linhas.find(x => x.nome === sel) : null;
  const corP = (p: number) => (p >= 90 ? 'bg-emerald-500' : p >= 75 ? 'bg-amber-400' : 'bg-red-400');

  if (r) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-[13px]">
          <button onClick={() => setSel(null)} className="text-gray-500 hover:text-gray-900">{por === 'resp' ? 'Responsáveis' : 'Setores'}</button>
          <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
          {por === 'resp' && sel !== '*' && <Avatar nome={r.nome} size={22} />}
          <span className="font-semibold">{sel === '*' ? 'Todas as ações' : por === 'resp' ? r.nome : frase(r.nome)}</span>
          <span className="text-gray-400">· {ano === 'todos' ? 'todo o período' : `incluídas em ${ano}`}</span>
        </div>
        <KpisSituacao l={r.l} f={fs} set={setFs} />
        <ListaAgrupada lista={ordenarAcoes(aplicaFSit(r.l, fs))} ctx={ctx} padrao="nenhum" vazio="Nenhuma ação com esse filtro." kpi={kpiDe(fs, setFs)} />
      </div>);
  }
  const abrirTodos = (f: FSit) => () => { setSel('*'); setTimeout(() => setFs(f), 0); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmentado valor={por} onChange={setPor} opcoes={[['resp', 'Responsável', <Users key="u" className="h-3.5 w-3.5" />], ['setor', 'Setor', <Layers key="l" className="h-3.5 w-3.5" />]]} />
        <select value={ano} onChange={e => setAno(e.target.value)} className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-700">
          <option value="todos">Todo o período</option>{anos.map(a => <option key={a} value={a}>Incluídas em {a}</option>)}</select>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <Kpi t="% concluído" v={`${pct(tot.c, tot.total)}%`} s={`${tot.c} de ${tot.total}`} tom={pct(tot.c, tot.total) >= 90 ? 'green' : undefined} />
        <Kpi t="Pendentes" v={tot.ab + tot.an + tot.atr} s={`${tot.ab} em aberto · ${tot.an} em andamento · ${tot.atr} atrasadas`} onClick={abrirTodos('pendentes')} />
        <Kpi t="Em aberto" v={tot.ab} onClick={abrirTodos('aberto')} /><Kpi t="Em andamento" v={tot.an} tom="indigo" onClick={abrirTodos('andamento')} />
        <Kpi t="Atrasadas" v={tot.atr} tom={tot.atr ? 'red' : undefined} onClick={abrirTodos('atrasadas')} /><Kpi t="Concluídas" v={tot.c} s={`de ${tot.total} ações`} tom="green" onClick={abrirTodos('concluido')} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[760px] text-[13px]">
          <thead><tr className="border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-400">
            <Th col="nome" t={por === 'resp' ? 'Responsável' : 'Setor'} esq />
            <Th col="pend" t="Pendentes" /><Th col="ab" t="Em aberto" /><Th col="an" t="Andamento" /><Th col="atr" t="Atrasadas" />
            <Th col="c" t="Concluído" /><Th col="total" t="Total" />
            <th onClick={() => setOrd(o => ({ col: 'p', desc: o.col === 'p' ? !o.desc : true }))} className={clsx('w-56 cursor-pointer select-none px-3 py-2 text-left font-medium hover:text-gray-700', ord.col === 'p' && 'text-gray-700')}>% concluído{ord.col === 'p' ? (ord.desc ? ' ↓' : ' ↑') : ''}</th></tr></thead>
          <tbody>
            {linhas.map(x => (
              <tr key={x.nome} onClick={() => setSel(x.nome)} className="h-10 cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50">
                <td className="px-3"><span className="flex items-center gap-2">{por === 'resp' && <Avatar nome={x.nome} size={22} />}<span className="font-medium text-gray-900">{por === 'resp' ? x.nome : frase(x.nome)}</span></span></td>
                <td className="px-2 text-right"><Num nome={x.nome} f="pendentes" v={x.pend} cls={x.pend ? 'font-semibold text-gray-900' : 'text-gray-300'} /></td>
                <td className="px-2 text-right"><Num nome={x.nome} f="aberto" v={x.ab} cls={x.ab ? 'font-medium text-amber-700' : 'text-gray-300'} /></td>
                <td className="px-2 text-right"><Num nome={x.nome} f="andamento" v={x.an} cls={x.an ? 'font-medium text-indigo-600' : 'text-gray-300'} /></td>
                <td className="px-2 text-right">{x.atr ? <button onClick={drill(x.nome, 'atrasadas')} title="Ver as atrasadas" className="inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 text-[12px] font-medium tabular-nums text-red-600 hover:underline"><AlertTriangle className="h-3 w-3" />{x.atr}</button> : <span className="text-gray-300">—</span>}</td>
                <td className="px-2 text-right"><Num nome={x.nome} f="concluido" v={x.c} cls="text-gray-500" /></td>
                <td className="px-2 text-right"><Num nome={x.nome} f="" v={x.total} cls="text-gray-500" /></td>
                <td className="px-3"><div className="flex items-center gap-2"><div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100"><i className={clsx('block h-full rounded-full', corP(x.p))} style={{ width: `${x.p}%` }} /></div><span className="w-9 text-right text-[12px] tabular-nums text-gray-600">{x.p}%</span></div></td>
              </tr>))}
          </tbody>
          <tfoot><tr className="h-10 border-t border-gray-200 text-[13px] font-semibold">
            <td className="px-3">Total</td><td className="px-2 text-right tabular-nums">{tot.ab + tot.an + tot.atr}</td><td className="px-2 text-right tabular-nums">{tot.ab}</td>
            <td className="px-2 text-right tabular-nums">{tot.an}</td><td className="px-2 text-right tabular-nums">{tot.atr}</td><td className="px-2 text-right tabular-nums">{tot.c}</td>
            <td className="px-2 text-right tabular-nums">{tot.total}</td><td className="px-3 text-[12px] tabular-nums">{pct(tot.c, tot.total)}%</td></tr></tfoot>
        </table>
      </div>
      <p className="text-[12px] text-gray-400">Clique no título da coluna para ordenar, num número para ver aquelas ações, ou na linha para ver tudo da pessoa. Canceladas ficam fora da conta.</p>
    </div>);
}

function MinhasAcoes({ ctx, colab }: { ctx: Ctx; colab?: Colaborador }) {
  // abre nas pendentes; os cartões trocam o filtro (clicar de novo / "% concluído" mostra todas)
  const [fs, setFs] = useState<FSit>('pendentes');
  if (!colab) return (
    <div className="rounded-lg border border-dashed border-gray-200 bg-white py-12 text-center text-[13px] text-gray-500">
      Seu usuário não está ligado a um colaborador do Escalone, então não dá para saber quais ações são suas.</div>);
  const id = String(colab.id || (colab as any)._id);
  const minhas = ctx.acoes.filter(a => a.respId === id && a.sit !== 'cancelado');
  const abertas = minhas.filter(a => a.sit === 'aberto' || a.sit === 'andamento');
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5"><Avatar nome={nomeProprio(colab.nome)} size={32} />
        <div><div className="text-[14px] font-semibold">{nomeProprio(colab.nome)}</div><div className="text-[12px] text-gray-500">{plural(abertas.length, 'ação pendente', 'ações pendentes')}</div></div></div>
      <KpisSituacao l={minhas} f={fs} set={setFs} pendentes />
      <ListaAgrupada lista={ordenarAcoes(aplicaFSit(minhas, fs))} ctx={ctx} padrao="nenhum" vazio="Nenhuma ação sua com esse filtro." kpi={kpiDe(fs, setFs)} />
    </div>);
}

// ---------- painel lateral ----------
type Nav = { pos: number; total: number; ir: (dir: 1 | -1) => void };
function Painel({ a, ctx, onFechar, nav }: { a: Acao; ctx: Ctx; onFechar: () => void; nav?: Nav }) {
  const [txt, setTxt] = useState('');
  // na reunião: depois de registrar / sem novidade / concluir, já abre a próxima da lista
  const [auto, setAuto] = useState(() => lerLocal('atas_auto_proxima', '1') === '1');
  useEffect(() => gravarLocal('atas_auto_proxima', auto ? '1' : '0'), [auto]);
  const [salvando, setSalvando] = useState(false);
  const [ok, setOk] = useState('');
  const r = ctx.reuniao;
  const hist = useMemo(() => entradas(a.andamento), [a.andamento]);
  const fimRef = useRef<HTMLDivElement>(null);
  // abre no topo (título e campos); só desce até o fim quando entra anotação nova
  const qtdAnterior = useRef(hist.length);
  useEffect(() => {
    if (hist.length > qtdAnterior.current) fimRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    qtdAnterior.current = hist.length;
  }, [hist.length]);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onFechar]);
  const temColab = (id: string) => ctx.opcoesResp.frequentes.some(c => String(c.id) === id) || ctx.opcoesResp.todos.some(c => String(c.id) === id);
  const fechada = a.sit === 'concluido' || a.sit === 'cancelado';
  const revista = !!r && a.ultima === r;

  const enviar = async (extra: Record<string, unknown> = {}, aviso = 'Salvo') => {
    if (salvando) return false;
    setSalvando(true);
    const body: Record<string, unknown> = { ...extra };
    if (txt.trim()) body.andamento = txt.trim();
    if (r) body.reuniao = r;
    const deu = await ctx.salvar(a.id, body);
    setSalvando(false);
    if (deu) {
      setTxt(''); setOk(aviso); setTimeout(() => setOk(''), 1800);
      if (r && auto && nav && !('prazo' in extra) && !('setor' in extra) && !('responsavelId' in extra)) setTimeout(() => nav.ir(1), 350);
    }
    return deu;
  };
  const meta = (rotulo: string, conteudo: ReactNode) => (
    <div className="grid grid-cols-[110px_1fr] items-center gap-2 py-1"><span className="text-[12px] text-gray-500">{rotulo}</span><div className="min-w-0">{conteudo}</div></div>);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-gray-900/10" onClick={onFechar} />
      <aside className="relative flex h-full w-full max-w-[520px] flex-col border-l border-gray-200 bg-white shadow-2xl shadow-gray-900/10">
        <header className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
          <StatusPill sit={a.sit} onMudar={s => enviar({ situacao: SIT[s].nome }, SIT[s].nome)} />
          <span className="text-[12px] text-gray-400">{frase(a.setor)}</span>
          {ok && <span className="inline-flex items-center gap-1 text-[12px] text-emerald-600"><Check className="h-3.5 w-3.5" />{ok}</span>}
          <div className="ml-auto flex items-center gap-1">
            {nav && nav.total > 0 && (
              <span className="mr-1 inline-flex items-center gap-0.5 text-[12px] tabular-nums text-gray-400">
                <button onClick={() => nav.ir(-1)} disabled={nav.pos <= 0} title="Anterior (K)" className="rounded-md p-1 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                {nav.pos >= 0 ? `${nav.pos + 1} de ${nav.total}` : `— de ${nav.total}`}
                <button onClick={() => nav.ir(1)} disabled={nav.pos >= nav.total - 1} title="Próxima (J)" className="rounded-md p-1 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </span>)}
            <button onClick={() => ctx.onAbrirCard(a.card)} title="Abrir o card completo" className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><ExternalLink className="h-4 w-4" /></button>
            <button onClick={onFechar} title="Fechar (Esc)" className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-4 w-4" /></button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-4">
            <h2 className="text-[16px] font-semibold leading-snug text-gray-900">{a.texto}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-gray-500">
              <PrazoChip a={a} />
              {!fechada && <span>{a.diasAberto} dias em aberto</span>}
              <span className="inline-flex items-center gap-1"><Repeat2 className="h-3 w-3" />{plural(a.vezes, 'reunião', 'reuniões')}</span>
              {revista && <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 text-emerald-700"><Check className="h-3 w-3" />revista nesta reunião</span>}
            </div>
          </div>

          <div className="mt-4 border-y border-gray-100 bg-gray-50/50 px-5 py-2">
            {meta('Responsável', <SelectResp value={respValor(a, temColab)} op={ctx.opcoesResp} onChange={v => v && enviar(respBody(v), 'Responsável alterado')} />)}
            {meta('Prazo', <input key={a.prazo} type="date" defaultValue={a.prazo} onBlur={e => e.target.value !== a.prazo && enviar({ prazo: e.target.value }, 'Prazo alterado')} className={campoCls} />)}
            {meta('Setor', <select value={a.setor} onChange={e => enviar({ setor: e.target.value }, 'Setor alterado')} className={campoCls}>
              {ctx.setores.map(s => <option key={s} value={s}>{frase(s)}</option>)}</select>)}
            {meta('Entrou na ata', <span className="text-[13px] text-gray-700">{br(a.origem)}</span>)}
            {a.sit === 'concluido' && a.conclusao && meta('Concluída em', <span className="text-[13px] text-gray-700">{br(a.conclusao)}{a.estimada && <span className="ml-1.5 text-[11px] text-amber-600" title="Sem anotação datada na planilha: data estimada pelo prazo">≈ estimada</span>}</span>)}
          </div>

          <div className="px-5 py-4">
            <div className="mb-3 text-[11px] font-medium uppercase tracking-wide text-gray-400">Andamento</div>
            {hist.length ? (
              <ol className="relative ml-1.5 border-l border-gray-200">
                {hist.map((h, i) => (
                  <li key={i} className="relative mb-3 pl-4 last:mb-0">
                    <span className={clsx('absolute -left-[5.5px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white', i === hist.length - 1 ? 'bg-indigo-500' : 'bg-gray-300')} />
                    <div className="text-[11px] font-medium tabular-nums text-gray-500">{h.data || 'sem data'}</div>
                    <div className="text-[13px] leading-relaxed text-gray-800">{frase(h.texto) || <span className="text-gray-400">—</span>}</div>
                  </li>))}
              </ol>) : <div className="text-[13px] text-gray-400">Nenhum andamento registrado ainda.</div>}
            <div ref={fimRef} />
          </div>
        </div>

        <footer className="border-t border-gray-100 px-5 py-3">
          <textarea value={txt} onChange={e => setTxt(e.target.value)} rows={2}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && txt.trim()) { e.preventDefault(); enviar(); } }}
            placeholder={r ? `Andamento na reunião de ${br(r)}…` : 'Registrar andamento…'}
            className="w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-[13px] focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button disabled={!txt.trim() || salvando} onClick={() => enviar()}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-gray-800 disabled:opacity-30">Registrar</button>
            {a.sit !== 'concluido' && (
              <button disabled={salvando} onClick={() => enviar({ situacao: 'Concluído' }, 'Concluída')}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[12px] font-medium text-emerald-700 hover:bg-emerald-100">
                <CheckCircle2 className="h-3.5 w-3.5" />Concluir</button>)}
            {r && !revista && !fechada && (
              <button disabled={salvando} onClick={() => enviar({ revisada: true }, 'Revista')} title="Revista na reunião, sem novidade (conta +1 reunião)"
                className="rounded-md border border-gray-200 px-3 py-1.5 text-[12px] text-gray-600 hover:bg-gray-50">Sem novidade</button>)}
            {a.sit !== 'cancelado' && (
              <button disabled={salvando} onClick={() => { if (window.confirm('Cancelar esta ação? O texto digitado vira o motivo.')) enviar({ situacao: 'Cancelado' }, 'Cancelada'); }}
                className="ml-auto rounded-md px-2 py-1.5 text-[12px] text-gray-400 hover:bg-red-50 hover:text-red-600">Cancelar ação</button>)}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
            <span>Ctrl+Enter registra · {r ? `vale para a reunião de ${br(r)}` : 'registrado com a data de hoje'} · J/K navega</span>
            {r && <label className="ml-auto inline-flex items-center gap-1.5"><input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} className="h-3 w-3 rounded border-gray-300" />ir para a próxima ao salvar</label>}
          </div>
        </footer>
      </aside>
    </div>);
}

function Modal({ titulo, onFechar, children, largura = 'max-w-lg' }: { titulo: ReactNode; onFechar: () => void; children: ReactNode; largura?: string }) {
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onFechar]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-gray-900/20 p-3 pt-[10vh]" onClick={onFechar}>
      <div className={clsx('w-full rounded-xl border border-gray-200 bg-white shadow-2xl', largura)} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          <div className="text-[13px] font-semibold text-gray-900">{titulo}</div>
          <button onClick={onFechar} className="ml-auto rounded-md p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>);
}

function NovaAcao({ ctx, reuniao, respPadrao, onFechar }: { ctx: Ctx; reuniao: string; respPadrao?: string; onFechar: () => void }) {
  const [acao, setAcao] = useState('');
  const [resp, setResp] = useState(respPadrao || '');
  const [prazo, setPrazo] = useState('');
  const [setor, setSetor] = useState(() => lerLocal('atas_setor', ''));
  const [salvando, setSalvando] = useState(false);
  const [criadas, setCriadas] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  const pode = !!(acao.trim() && resp && setor && !salvando);
  const enviar = async () => {
    if (!pode) return;
    setSalvando(true);
    gravarLocal('atas_setor', setor);
    const deu = await ctx.criar({ acao: acao.trim(), ...respBody(resp), prazo, setor, reuniao });
    setSalvando(false);
    if (deu) { setAcao(''); setPrazo(''); setCriadas(n => n + 1); ref.current?.focus(); }
  };
  return (
    <Modal titulo={<span className="flex items-center gap-2"><Plus className="h-4 w-4" />Nova ação <span className="font-normal text-gray-400">· reunião de {br(reuniao)}</span></span>} onFechar={onFechar}>
      <div className="space-y-3 p-4">
        <textarea ref={ref} autoFocus rows={2} value={acao} onChange={e => setAcao(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          placeholder="O que precisa ser feito?" className="w-full resize-none border-0 p-0 text-[15px] placeholder:text-gray-300 focus:outline-none focus:ring-0" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <SelectResp value={resp} onChange={setResp} op={ctx.opcoesResp} />
          <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)} title="Prazo" className={campoCls} />
          <select value={setor} onChange={e => setSetor(e.target.value)} className={campoCls}>
            <option value="">Setor…</option>{ctx.setores.map(s => <option key={s} value={s}>{frase(s)}</option>)}</select>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-2.5">
        <span className="text-[12px] text-gray-400">{criadas ? `${plural(criadas, 'ação criada', 'ações criadas')} · ` : ''}Enter cria e continua</span>
        <button onClick={onFechar} className="ml-auto rounded-md px-3 py-1.5 text-[12px] text-gray-600 hover:bg-gray-100">Fechar</button>
        <button disabled={!pode} onClick={enviar} className="rounded-md bg-gray-900 px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-30">{salvando ? 'Criando…' : 'Criar ação'}</button>
      </div>
    </Modal>);
}

function GerarAta({ ctx, reuniao, onFechar }: { ctx: Ctx; reuniao: string; onFechar: () => void }) {
  const [copiado, setCopiado] = useState(false);
  const l = ctx.acoes;
  const linha = (a: Acao, extra = '') => `• [${frase(a.setor)}] ${a.respNome} — ${a.texto}${extra}`;
  const novas = l.filter(a => a.origem === reuniao);
  const concl = l.filter(a => a.sit === 'concluido' && a.ultima === reuniao && a.origem !== reuniao);
  const canc = l.filter(a => a.sit === 'cancelado' && a.ultima === reuniao);
  const revistas = l.filter(a => a.ultima === reuniao && a.origem !== reuniao && !concl.includes(a) && !canc.includes(a));
  const abertas = l.filter(a => a.sit === 'aberto' || a.sit === 'andamento');
  const atras = ordenarAcoes(abertas.filter(a => a.atrasada));
  const dia = (a: Acao) => entradaDoDia(a.andamento, reuniao);
  const blocos: [string, string[]][] = [
    [`Novas ações (${novas.length})`, novas.map(a => linha(a, a.prazo ? ` — prazo ${br(a.prazo)}` : ''))],
    [`Concluídas (${concl.length})`, concl.map(a => linha(a, dia(a) ? `: ${dia(a)}` : ''))],
    [`Andamento registrado (${revistas.length})`, revistas.map(a => linha(a, `${dia(a) ? ': ' + dia(a) : ''}${a.prazo ? ` [prazo ${br(a.prazo)}]` : ''}`))],
    ...(canc.length ? [[`Canceladas (${canc.length})`, canc.map(a => linha(a))] as [string, string[]]] : []),
    [`Pendências: ${abertas.length} em aberto, ${plural(atras.length, 'atrasada', 'atrasadas')}`, atras.map(a => linha(a, ` — venceu ${br(a.prazo)} (${a.diasAtraso} dias)`))],
  ];
  const texto = [`ATA DE REUNIÃO — ${br(reuniao)}`, 'Plano de Ação – Atas (Escalone)', '',
    ...blocos.flatMap(([t, ls]) => [t.toUpperCase(), ...(ls.length ? ls : ['• nenhuma']), ''])].join('\n');
  const copiar = async () => { try { await navigator.clipboard.writeText(texto); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sem permissão */ } };
  const imprimir = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Ata ${br(reuniao)}</title><style>
      body{font:13px/1.55 Inter,system-ui,Segoe UI,Arial;margin:36px;color:#111827}h1{font-size:20px;margin:0}small{color:#6b7280}
      h2{font-size:12px;margin:22px 0 6px;padding-bottom:4px;border-bottom:1px solid #e5e7eb;letter-spacing:.06em;text-transform:uppercase;color:#374151}
      ul{margin:0;padding-left:18px}li{margin:3px 0}@page{size:A4;margin:14mm}</style></head><body>
      <h1>Ata de reunião — ${br(reuniao)}</h1><small>Plano de Ação – Atas · Escalone</small>
      ${blocos.map(([t, ls]) => `<h2>${esc(t)}</h2><ul>${(ls.length ? ls : ['• nenhuma']).map(x => `<li>${esc(x.replace(/^• /, ''))}</li>`).join('')}</ul>`).join('')}
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };
  return (
    <Modal largura="max-w-3xl" onFechar={onFechar} titulo={<span className="flex items-center gap-2"><FileText className="h-4 w-4" />Ata de {br(reuniao)}
      <span className="font-normal text-gray-400">· {novas.length} novas · {concl.length} concluídas · {revistas.length} com andamento</span></span>}>
      <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap bg-gray-50 p-5 font-mono text-[12px] leading-relaxed text-gray-800">{texto}</pre>
      <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-2.5">
        <button onClick={imprimir} className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-[12px] hover:bg-gray-50"><Printer className="h-3.5 w-3.5" />Imprimir / PDF</button>
        <button onClick={copiar} className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-[12px] font-medium text-white">{copiado ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copiado ? 'Copiado' : 'Copiar texto'}</button>
      </div>
    </Modal>);
}

// ---------- indicadores (SLA histórico) ----------
// Tempo de resolução = Data de inclusão → data de conclusão. Conclusão "estimada": importada sem anotação
// datada na planilha (a importação usou o prazo ou a inclusão) ou sem "Concluído em" (usa a movimentação).
type FiltroInd = { tipo: 'aging' | 'mes' | 'setor' | 'resp' | 'abertas' | 'atrasadas' | 'concluidas' | 'noprazo' | 'foraprazo'; valor: string; rotulo: string } | null;
const FAIXAS: { k: string; l: string; min: number; max: number; cor: string }[] = [
  { k: '0-30', l: '0–30 dias', min: 0, max: 30, cor: 'bg-emerald-500' },
  { k: '31-90', l: '31–90 dias', min: 31, max: 90, cor: 'bg-amber-400' },
  { k: '91-180', l: '91–180 dias', min: 91, max: 180, cor: 'bg-orange-500' },
  { k: '180+', l: 'Mais de 180 dias', min: 181, max: Infinity, cor: 'bg-red-500' },
];
const mediana = (l: number[]) => { if (!l.length) return 0; const s = [...l].sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2); };
const media = (l: number[]) => (l.length ? Math.round(l.reduce((s, x) => s + x, 0) / l.length) : 0);
const MES_CURTO = (k: string) => `${MESES[Number(k.slice(5, 7)) - 1]}/${k.slice(2, 4)}`;

function Indicadores({ ctx }: { ctx: Ctx }) {
  const [ano, setAno] = useState('todos');
  const [setor, setSetor] = useState('');
  const [resp, setResp] = useState('');
  const [semEstimadas, setSemEstimadas] = useState(false);
  const [f, setF] = useState<FiltroInd>(null);
  const [mesHover, setMesHover] = useState<string | null>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const graficoRef = useRef<HTMLDivElement>(null);
  const filtrar = (x: FiltroInd) => {
    const igual = f && x && f.tipo === x.tipo && f.valor === x.valor;
    setF(igual ? null : x);
    if (!igual && x) setTimeout(() => listaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  const anos = useMemo(() => [...new Set(ctx.acoes.map(a => a.inclusao.slice(0, 4)).filter(Boolean))].sort().reverse(), [ctx.acoes]);
  const setoresL = useMemo(() => [...new Set(ctx.acoes.map(a => a.setor))].sort(), [ctx.acoes]);

  const respsL = useMemo(() => [...new Set(ctx.acoes.map(a => a.respNome))].sort().map(s => ({ v: s, l: s, av: true })), [ctx.acoes]);
  const doSetor = ctx.acoes.filter(a => a.sit !== 'cancelado' && (!setor || a.setor === setor) && (!resp || a.respNome === resp));
  const base = doSetor.filter(a => ano === 'todos' || a.inclusao.startsWith(ano));
  const dur = (a: Acao) => Math.max(0, dias(a.inclusao, a.conclusao));
  const ehConcl = (a: Acao) => a.sit === 'concluido' && !!a.conclusao && !!a.inclusao && (!semEstimadas || !a.estimada);
  const concl = base.filter(ehConcl);
  const estimadas = base.filter(a => a.sit === 'concluido' && a.estimada).length;
  const abertas = base.filter(a => a.sit === 'aberto' || a.sit === 'andamento');
  const atrasadas = abertas.filter(a => a.atrasada);
  const noPrazo = (a: Acao) => !!a.prazo && a.conclusao <= a.prazo;
  const comPrazo = concl.filter(a => a.prazo);
  const pctPrazo = pct(comPrazo.filter(noPrazo).length, comPrazo.length);
  const ds = concl.map(dur);

  // evolução mês a mês (todos os anos, ou só o ano escolhido)
  const meses = useMemo(() => {
    const todos = doSetor.flatMap(a => [a.inclusao.slice(0, 7), ehConcl(a) ? a.conclusao.slice(0, 7) : '']).filter(k => /^\d{4}-\d{2}$/.test(k)).sort();
    if (!todos.length) return [];
    const ini = ano !== 'todos' ? `${ano}-01` : todos[0];
    const fimAno = ano !== 'todos' ? `${ano}-12` : ctx.hoje.slice(0, 7);
    const fim = fimAno > ctx.hoje.slice(0, 7) ? ctx.hoje.slice(0, 7) : fimAno;
    const out: { k: string; inc: number; conc: number; media: number }[] = [];
    let [y, m] = ini.split('-').map(Number);
    for (let guard = 0; guard < 120; guard++) {
      const k = `${y}-${String(m).padStart(2, '0')}`;
      if (k > fim) break;
      const cs = doSetor.filter(a => ehConcl(a) && a.conclusao.startsWith(k));
      out.push({ k, inc: doSetor.filter(a => a.inclusao.startsWith(k)).length, conc: cs.length, media: media(cs.map(dur)) });
      m++; if (m > 12) { m = 1; y++; }
    }
    return out;
  }, [doSetor, ano, semEstimadas, ctx.hoje]); // eslint-disable-line react-hooks/exhaustive-deps

  const porSetor = useMemo(() => {
    const m = new Map<string, Acao[]>();
    base.forEach(a => m.set(a.setor, [...(m.get(a.setor) || []), a]));
    return [...m.entries()].map(([nome, l]) => {
      const c = l.filter(ehConcl); const cp = c.filter(a => a.prazo);
      return { nome, abertas: l.filter(a => a.sit === 'aberto' || a.sit === 'andamento').length, atr: l.filter(a => a.atrasada).length,
        n: c.length, media: media(c.map(dur)), mediana: mediana(c.map(dur)), prazo: cp.length ? pct(cp.filter(noPrazo).length, cp.length) : null };
    }).sort((x, y) => y.abertas - x.abertas || y.n - x.n);
  }, [base, semEstimadas]); // eslint-disable-line react-hooks/exhaustive-deps

  const ranking = useMemo(() => {
    const m = new Map<string, Acao[]>();
    base.forEach(a => m.set(a.respNome, [...(m.get(a.respNome) || []), a]));
    return [...m.entries()].map(([nome, l]) => {
      const c = l.filter(ehConcl); const cp = c.filter(a => a.prazo);
      return { nome, total: l.length, abertas: l.filter(a => a.sit === 'aberto' || a.sit === 'andamento').length, atr: l.filter(a => a.atrasada).length,
        media: c.length ? media(c.map(dur)) : null, prazo: cp.length ? pct(cp.filter(noPrazo).length, cp.length) : null,
        vezes: Math.round((l.reduce((s, a) => s + a.vezes, 0) / l.length) * 10) / 10 };
    }).sort((x, y) => y.abertas - x.abertas || y.atr - x.atr || y.total - x.total);
  }, [base, semEstimadas]); // eslint-disable-line react-hooks/exhaustive-deps

  const lista = !f ? [] : (() => {
    const v = f.valor;
    let l: Acao[] = [];
    if (f.tipo === 'aging') { const fx = FAIXAS.find(x => x.k === v)!; l = abertas.filter(a => a.diasAberto >= fx.min && a.diasAberto <= fx.max); }
    else if (f.tipo === 'abertas') l = abertas;
    else if (f.tipo === 'atrasadas') l = atrasadas;
    else if (f.tipo === 'concluidas') l = concl;
    else if (f.tipo === 'noprazo') l = comPrazo.filter(noPrazo);
    else if (f.tipo === 'foraprazo') l = comPrazo.filter(a => !noPrazo(a));
    else if (f.tipo === 'setor') l = base.filter(a => a.setor === v && (a.sit === 'aberto' || a.sit === 'andamento' || ehConcl(a)));
    else if (f.tipo === 'resp') l = base.filter(a => a.respNome === v);
    else if (f.tipo === 'mes') l = doSetor.filter(a => a.inclusao.startsWith(v) || (ehConcl(a) && a.conclusao.startsWith(v)));
    // mais antigas (abertas há mais tempo) no topo
    return [...l].sort((x, y) => Number(y.sit === 'aberto' || y.sit === 'andamento') - Number(x.sit === 'aberto' || x.sit === 'andamento') || y.diasAberto - x.diasAberto);
  })();

  // gráfico (abre mostrando os meses mais recentes)
  useEffect(() => { const g = graficoRef.current; if (g) g.scrollLeft = g.scrollWidth; }, [meses.length]);
  const H = 170, PAD = 22, BW = 26;
  const W = Math.max(meses.length * BW + 40, 300);
  const maxQ = Math.max(1, ...meses.map(x => Math.max(x.inc, x.conc)));
  const maxD = Math.max(1, ...meses.map(x => x.media));
  const y = (q: number) => H - PAD - (q / maxQ) * (H - PAD - 10);
  const yd = (d: number) => H - PAD - (d / maxD) * (H - PAD - 10);
  const pontos = meses.map((x, i) => (x.conc ? `${20 + i * BW + BW / 2},${yd(x.media)}` : null)).filter(Boolean).join(' ');
  const hover = meses.find(x => x.k === mesHover);
  const ativo = (tipo: string, valor = '') => !!f && f.tipo === tipo && f.valor === valor;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select value={ano} onChange={e => { setAno(e.target.value); setF(null); }} className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-700">
          <option value="todos">Todo o período</option>{anos.map(a => <option key={a} value={a}>Incluídas em {a}</option>)}</select>
        <select value={setor} onChange={e => { setSetor(e.target.value); setF(null); }} className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-700">
          <option value="">Todos os setores</option>{setoresL.map(s => <option key={s} value={s}>{frase(s)}</option>)}</select>
        <ChipFiltro rotulo="Responsável" valor={resp} onChange={v => { setResp(v); setF(null); }} opcoes={respsL} icone={<UserRound className="h-3 w-3" />} />
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-600"
          title="Conclusões importadas sem anotação datada na planilha: a data usada foi o prazo (ou a inclusão)">
          <input type="checkbox" checked={!semEstimadas} onChange={e => setSemEstimadas(!e.target.checked)} className="h-3 w-3 rounded border-gray-300" />
          incluir conclusões com data estimada ({estimadas})</label>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi t="Tempo médio de resolução" v={`${media(ds)} d`} s={`mediana ${mediana(ds)} d · ${concl.length} concluídas`} onClick={() => filtrar({ tipo: 'concluidas', valor: '', rotulo: 'Concluídas' })} ativo={ativo('concluidas')} />
        <Kpi t="Concluídas no prazo" v={`${pctPrazo}%`} s={`${comPrazo.filter(noPrazo).length} de ${comPrazo.length} com prazo`} tom={pctPrazo >= 80 ? 'green' : pctPrazo < 60 ? 'red' : undefined} onClick={() => filtrar({ tipo: 'noprazo', valor: '', rotulo: 'Concluídas no prazo' })} ativo={ativo('noprazo')} />
        <Kpi t="Concluídas fora do prazo" v={comPrazo.length - comPrazo.filter(noPrazo).length} s="depois do prazo" tom="red" onClick={() => filtrar({ tipo: 'foraprazo', valor: '', rotulo: 'Concluídas fora do prazo' })} ativo={ativo('foraprazo')} />
        <Kpi t="Em aberto" v={abertas.length} s={`há ${media(abertas.map(a => a.diasAberto))} dias em média`} onClick={() => filtrar({ tipo: 'abertas', valor: '', rotulo: 'Em aberto' })} ativo={ativo('abertas')} />
        <Kpi t="Atrasadas" v={atrasadas.length} s={`${pct(atrasadas.length, abertas.length)}% das abertas`} tom={atrasadas.length ? 'red' : undefined} onClick={() => filtrar({ tipo: 'atrasadas', valor: '', rotulo: 'Atrasadas' })} ativo={ativo('atrasadas')} />
        <Kpi t="Reincidência média" v={`${(Math.round(base.reduce((s, a) => s + a.vezes, 0) / Math.max(1, base.length) * 10) / 10).toString().replace('.', ',')}×`} s="vezes na ata por ação" />
      </div>

      <div className="grid gap-3 lg:grid-cols-[340px_minmax(0,1fr)]">
        <section className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-gray-800"><Clock className="h-3.5 w-3.5 text-gray-400" />Em aberto por idade</div>
          <div className="space-y-1.5">
            {FAIXAS.map(fx => {
              const n = abertas.filter(a => a.diasAberto >= fx.min && a.diasAberto <= fx.max).length;
              return (
                <button key={fx.k} onClick={() => filtrar({ tipo: 'aging', valor: fx.k, rotulo: `Em aberto · ${fx.l}` })}
                  className={clsx('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-gray-50', ativo('aging', fx.k) && 'bg-gray-100 ring-1 ring-gray-900')}>
                  <span className="w-28 text-gray-600">{fx.l}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100"><i className={clsx('block h-full rounded-full', fx.cor)} style={{ width: `${pct(n, Math.max(1, abertas.length))}%` }} /></span>
                  <span className="w-8 text-right font-semibold tabular-nums text-gray-900">{n}</span>
                </button>);
            })}
          </div>
          <p className="mt-2 text-[11px] text-gray-400">Idade = dias desde a inclusão na ata. Clique para ver as ações (mais antigas primeiro).</p>
        </section>

        <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-1 flex flex-wrap items-center gap-3 text-[13px] font-semibold text-gray-800">
            <span>Evolução mês a mês</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-normal text-gray-500"><i className="inline-block h-2 w-2 rounded-sm bg-indigo-300" />incluídas</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-normal text-gray-500"><i className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />concluídas</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-normal text-gray-500"><i className="inline-block h-0.5 w-3 bg-orange-500" />tempo médio de resolução (dias)</span>
            <span className="ml-auto text-[11px] font-normal text-gray-500">
              {hover ? `${MES_CURTO(hover.k)}: ${hover.inc} incluídas · ${hover.conc} concluídas · ${hover.conc ? hover.media + ' d em média' : 'sem conclusões'}` : 'passe o mouse ou clique num mês'}</span>
          </div>
          <div ref={graficoRef} className="overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} className="h-44 min-w-full" style={{ width: Math.max(W, 300) }} onMouseLeave={() => setMesHover(null)}>
              {[0.5, 1].map(p => <line key={p} x1={20} x2={W - 10} y1={y(maxQ * p)} y2={y(maxQ * p)} stroke="#f3f4f6" />)}
              <text x={2} y={y(maxQ) + 3} fontSize="8" fill="#9ca3af">{maxQ}</text>
              <text x={W - 2} y={yd(maxD) + 3} fontSize="8" fill="#f97316" textAnchor="end">{maxD}d</text>
              {meses.map((x, i) => {
                const x0 = 20 + i * BW;
                const sel = ativo('mes', x.k);
                return (
                  <g key={x.k} onMouseEnter={() => setMesHover(x.k)} onClick={() => filtrar({ tipo: 'mes', valor: x.k, rotulo: `Incluídas ou concluídas em ${MES_CURTO(x.k)}` })} className="cursor-pointer">
                    <rect x={x0} y={8} width={BW} height={H - PAD - 8} fill={sel || mesHover === x.k ? '#f3f4f6' : 'transparent'} />
                    <rect x={x0 + 3} y={y(x.inc)} width={(BW - 8) / 2} height={H - PAD - y(x.inc)} rx={1.5} fill="#a5b4fc" />
                    <rect x={x0 + 3 + (BW - 8) / 2 + 2} y={y(x.conc)} width={(BW - 8) / 2} height={H - PAD - y(x.conc)} rx={1.5} fill="#10b981" />
                    {(i % 3 === 0 || meses.length <= 14) && <text x={x0 + BW / 2} y={H - 8} fontSize="8" fill="#6b7280" textAnchor="middle">{MES_CURTO(x.k)}</text>}
                    <title>{`${MES_CURTO(x.k)}: ${x.inc} incluídas, ${x.conc} concluídas${x.conc ? `, ${x.media} dias em média` : ''}`}</title>
                  </g>);
              })}
              {pontos && <polyline points={pontos} fill="none" stroke="#f97316" strokeWidth={1.5} strokeLinejoin="round" pointerEvents="none" />}
              {meses.map((x, i) => x.conc ? <circle key={x.k} cx={20 + i * BW + BW / 2} cy={yd(x.media)} r={2} fill="#f97316" pointerEvents="none" /> : null)}
            </svg>
          </div>
        </section>
      </div>

      <div className="atas-tabelas grid gap-3 xl:grid-cols-2 [&>*]:min-w-0">
        <section className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <div className="px-3 pt-3 text-[13px] font-semibold text-gray-800">Por setor</div>
          <table className="w-full min-w-[520px] text-[13px]">
            <thead><tr className="border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-400">
              <th className="px-3 py-2 text-left font-medium">Setor</th><th className="px-2 py-2 text-right font-medium">Abertas</th>
              <th className="px-2 py-2 text-right font-medium">Atras.</th><th className="px-2 py-2 text-right font-medium">Concl.</th>
              <th className="px-2 py-2 text-right font-medium">Média</th><th className="px-2 py-2 text-right font-medium">Mediana</th><th className="px-3 py-2 text-right font-medium">No prazo</th></tr></thead>
            <tbody>{porSetor.map(x => (
              <tr key={x.nome} onClick={() => filtrar({ tipo: 'setor', valor: x.nome, rotulo: `Setor: ${frase(x.nome)}` })}
                className={clsx('h-9 cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50', ativo('setor', x.nome) && 'bg-gray-100')}>
                <td className="px-3 font-medium text-gray-900">{frase(x.nome)}</td>
                <td className={clsx('px-2 text-right tabular-nums', x.abertas ? 'font-medium text-amber-700' : 'text-gray-300')}>{x.abertas}</td>
                <td className={clsx('px-2 text-right tabular-nums', x.atr ? 'font-medium text-red-600' : 'text-gray-300')}>{x.atr || '—'}</td>
                <td className="px-2 text-right tabular-nums text-gray-500">{x.n}</td>
                <td className="px-2 text-right tabular-nums">{x.n ? `${x.media} d` : '—'}</td>
                <td className="px-2 text-right tabular-nums text-gray-500">{x.n ? `${x.mediana} d` : '—'}</td>
                <td className="px-3 text-right tabular-nums">{x.prazo == null ? '—' : <span className={x.prazo >= 80 ? 'text-emerald-600' : x.prazo < 60 ? 'text-red-600' : 'text-amber-600'}>{x.prazo}%</span>}</td>
              </tr>))}</tbody>
          </table>
        </section>
        <section className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <div className="px-3 pt-3 text-[13px] font-semibold text-gray-800">Ranking por responsável</div>
          <table className="w-full min-w-[560px] text-[13px]">
            <thead><tr className="border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-400">
              <th className="px-3 py-2 text-left font-medium">Responsável</th><th className="px-2 py-2 text-right font-medium">Abertas</th>
              <th className="px-2 py-2 text-right font-medium">Atras.</th><th className="px-2 py-2 text-right font-medium">Tempo médio</th>
              <th className="px-2 py-2 text-right font-medium">No prazo</th><th className="px-3 py-2 text-right font-medium" title="Média de vezes na ata por ação">Reincid.</th></tr></thead>
            <tbody>{ranking.map(x => (
              <tr key={x.nome} onClick={() => { setResp(resp === x.nome ? '' : x.nome); setF(null); }} title={resp === x.nome ? 'Tirar o filtro desta pessoa' : 'Ver os indicadores só desta pessoa'}
                className={clsx('h-9 cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50', resp === x.nome && 'bg-gray-100')}>
                <td className="px-3"><span className="flex items-center gap-2"><Avatar nome={x.nome} /><span className="font-medium text-gray-900">{x.nome}</span></span></td>
                <td className={clsx('px-2 text-right tabular-nums', x.abertas ? 'font-medium text-amber-700' : 'text-gray-300')}>{x.abertas}</td>
                <td className={clsx('px-2 text-right tabular-nums', x.atr ? 'font-medium text-red-600' : 'text-gray-300')}>{x.atr || '—'}</td>
                <td className="px-2 text-right tabular-nums">{x.media == null ? '—' : `${x.media} d`}</td>
                <td className="px-2 text-right tabular-nums">{x.prazo == null ? '—' : <span className={x.prazo >= 80 ? 'text-emerald-600' : x.prazo < 60 ? 'text-red-600' : 'text-amber-600'}>{x.prazo}%</span>}</td>
                <td className={clsx('px-3 text-right tabular-nums', x.vezes >= 4 ? 'text-orange-600 font-medium' : 'text-gray-500')}>{String(x.vezes).replace('.', ',')}×</td>
              </tr>))}</tbody>
          </table>
        </section>
      </div>

      <div ref={listaRef} className="scroll-mt-20">
        {f ? <ListaAgrupada lista={lista} ctx={ctx} padrao="nenhum" vazio="Nenhuma ação nesse recorte." kpi={{ rotulo: f.rotulo, limpar: () => setF(null) }} />
          : <div className="rounded-lg border border-dashed border-gray-200 bg-white py-8 text-center text-[13px] text-gray-400">Clique num cartão, faixa de idade, mês, setor ou responsável para ver as ações.</div>}
      </div>
      <p className="text-[11px] text-gray-400">
        Tempo de resolução = da inclusão na ata até a conclusão. {estimadas} conclusões importadas não tinham anotação datada na planilha e usam o prazo (ou a inclusão) como data estimada; desmarque acima para tirá-las da conta. Nas ações importadas, a conclusão é a data da reunião em que ela foi anotada como concluída (pode ser dias depois da entrega real). Canceladas ficam fora.</p>
    </div>);
}
